import styled from 'styled-components';
import { Tabs as RTabs, Tab, TabList, TabPanel, resetIdCounter } from 'react-tabs';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { ITabName } from 'checkout/types/Tab';
import { variableFontMedium } from 'styles/theme/text';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { PaymentTab } from 'checkout/components/Tabs/PaymentTab';
import { ShippingTab } from 'checkout/components/Tabs/ShippingTab';
import { ReviewTab } from 'checkout/components/Tabs/ReviewTab';
import { guestPurchasingGiftCard, purchasingGiftCard } from 'checkout/utils/giftCards';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Tabs.Tabs';

export const Tabs = () => {
  const router = useRouter();

  const { changeTab, sections, tab, order, isGuestCheckout } = useCheckoutContext();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);

  const isWebEnableCheckoutGiftCardsAllowGuestPurchase = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_PURCHASE,
  );
  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );

  const isGuestPurchasingGiftCard = guestPurchasingGiftCard({
    giftCardId: router?.query?.giftCardId,
    isAuthenticated,
    isGuestCheckout,
    isWebEnableCheckoutGiftCardsAllowGuestPurchase,
  });
  const isPurchasingGiftCard = purchasingGiftCard({
    giftCardId: order?.product?.giftCardId,
    isGuestPurchasingGiftCard,
    isCheckoutPurchaseGiftCardsEnabled,
  });

  const orderedTabList = [ITabName.SHIPPING, ITabName.PAYMENT, ITabName.REVIEW];

  if (isPurchasingGiftCard) {
    orderedTabList.shift();
  }

  const tabIndex = {
    [ITabName.SHIPPING]: 0,
    [ITabName.PAYMENT]: 1,
    [ITabName.REVIEW]: 2,
  };

  const tabLabels = {
    [ITabName.SHIPPING]: t(`${TP}.shippingTabLabel`, 'Shipping'),
    [ITabName.PAYMENT]: t(`${TP}.paymentTabLabel`, 'Payment'),
    [ITabName.REVIEW]: t(`${TP}.reviewTabLabel`, 'Review'),
  };

  const handleTabSelect = (index: ITabName) => {
    // NOTE: gift card orders do not have shipping
    const selectedTabIndex = isPurchasingGiftCard ? index + 1 : index;

    switch (selectedTabIndex) {
      case ITabName.SHIPPING:
        changeTab(ITabName.SHIPPING);
        break;
      case ITabName.PAYMENT:
        if (sections.shippingAddress.complete && sections.shippingMethod.complete) {
          changeTab(ITabName.PAYMENT);
        }
        break;
      case ITabName.REVIEW:
        if (
          sections.shippingAddress.complete &&
          sections.shippingMethod.complete &&
          sections.payment.complete
        ) {
          changeTab(ITabName.REVIEW);
        }
        break;
      default:
    }
  };

  // NOTE: ensures ids matches for SSR and client
  resetIdCounter();

  useEffect(() => {
    if (isGuestPurchasingGiftCard) {
      changeTab(ITabName.PAYMENT);
    }
  }, [isGuestPurchasingGiftCard]);
  return (
    <RTabs
      data-qa="checkout_tabs"
      selectedTabClassName="checkout-tab-selected"
      selectedTabPanelClassName="checkout-tab-panel-selected"
      onSelect={handleTabSelect}
      selectedIndex={isPurchasingGiftCard ? tabIndex[tab] - 1 : tabIndex[tab]}
    >
      <Tabs.TabList>
        {orderedTabList.map((tabEnum) => (
          <Tabs.Tab
            aria-label={tabLabels[tabEnum]}
            data-qa={`checkout_${tabLabels[tabEnum].toLowerCase()}_tab`}
            key={tabEnum}
          >
            {tabLabels[tabEnum]}
          </Tabs.Tab>
        ))}
      </Tabs.TabList>

      {!isPurchasingGiftCard && (
        <Tabs.TabPanel>
          <ShippingTab />
        </Tabs.TabPanel>
      )}
      <Tabs.TabPanel>
        <PaymentTab />
      </Tabs.TabPanel>
      <Tabs.TabPanel>
        <ReviewTab />
      </Tabs.TabPanel>
    </RTabs>
  );
};

Tabs.Tab = styled(Tab)`
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;
  display: inline-block;
  list-style: none;
  margin-right: 12px;
  position: relative;
  ${variableFontMedium({ size: '12.5px', height: '12.5px' })};
  padding: 8px 0 10px 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    padding: 8px 0 2px 0;
    margin-right: 14px;

    &.checkout-tab-selected {
      text-decoration: underline;
    }
  }

  &:focus {
    outline: dotted 1px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    &.checkout-tab-selected {
      border-bottom: 2px solid ${({ theme }) => theme.colors.offBlack};
    }
  }
`;

Tabs.TabList = styled(TabList)`
  padding: 0 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black};
  margin: 0 0 22px;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    border-bottom: 0;
    padding: 0;
    margin-bottom: 48px;
  }
`;

Tabs.TabPanel = styled(TabPanel)`
  display: none;
  padding: 0 16px;

  &.checkout-tab-panel-selected {
    display: block;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    border-bottom: 0;
    padding: 0;
  }
`;
